import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  TextInput,
  Button,
  StyleSheet,
  Alert,
  Switch,
  ActivityIndicator,
  Modal,
  TouchableOpacity,
  ScrollView,
} from "react-native";
import { TSession } from "../../../../../utils/types";
import {
  createIntegration,
  TCreateIntegrationRequest,
} from "../../../../../services/api-integrations";
import {
  IntegrationType,
  TWebhookIntegrationConfig,
  TWebhookFieldMapping,
} from "../../../../../utils/integrationTypes";

type Props = {
  session: TSession;
  onSuccess?: () => void;
};

type THeaderRow = {
  id: string;
  key: string;
  value: string;
};

type TFieldMappingRow = {
  id: string;
  target_field: string;
  source_path: string;
  default_value: string;
  literal_value: string;
  include_if_none: boolean;
};

type TWebhookTemplate = {
  id: string;
  name: string;
  description: string;
  config: TWebhookIntegrationConfig;
};

type TSourcePathOption = {
  value: string;
  label: string;
  description?: string;
};

type TSourcePathCategory = {
  id: string;
  title: string;
  options: TSourcePathOption[];
};

const REQUIRED_SOURCE_PATHS = [
  "patient_record.first_name",
  "patient_record.last_name",
  "patient_record.phone",
  "patient_record.location",
  "patient_record.procedures_of_interest",
  "photo_urls[0]",
  "photo_urls[1]",
];

const DEFAULT_SAMPLE_SUBMISSION = {
  clinic_uuid: "clinic-123",
  form_data: {
    formName: "Consultation Intake",
    submitted_at: "2024-01-01T09:30:00Z",
  },
  patient_record: {
    first_name: "Avery",
    last_name: "Stone",
    phone: "+1 555-0100",
    email: "avery.stone@example.com",
    location: "Main Clinic",
    procedures_of_interest: ["Botox", "Fillers"],
    photo_urls: [
      "https://cdn.clinicos.app/forms/photo-1.jpg",
      "https://cdn.clinicos.app/forms/photo-2.jpg",
    ],
    notes: "Interested in Botox treatment.",
  },
  patient: {
    gender: "female",
    date_of_birth: "1995-08-12",
  },
  photo_urls: [
    "https://cdn.clinicos.app/forms/photo-1.jpg",
    "https://cdn.clinicos.app/forms/photo-2.jpg",
  ],
};

const buildDefaultMappings = (): TFieldMappingRow[] =>
  REQUIRED_SOURCE_PATHS.map((path) => ({
    id: makeId("mapping"),
    target_field: "",
    source_path: path,
    default_value: "",
    literal_value: "",
    include_if_none: false,
  }));

const ensureRequiredMappings = (
  rows: TFieldMappingRow[],
): TFieldMappingRow[] => {
  const existingPaths = new Set(
    rows.map((row) => row.source_path.trim()).filter(Boolean),
  );
  const missing = REQUIRED_SOURCE_PATHS.filter(
    (path) => !existingPaths.has(path),
  ).map((path) => ({
    id: makeId("mapping"),
    target_field: "",
    source_path: path,
    default_value: "",
    literal_value: "",
    include_if_none: false,
  }));
  return [...rows, ...missing];
};

const WEBHOOK_TEMPLATES: TWebhookTemplate[] = [
  {
    id: "leadconnector",
    name: "LeadConnector CRM",
    description: "Send form submissions to CRM automatically.",
    config: {
      webhook_url:
        "https://services.leadconnectorhq.com/hooks/abc123/webhook-trigger/xyz",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer leadconnector-internal-token",
      },
      field_mappings: [
        {
          target_field: "contact.firstName",
          source_path: "patient_record.first_name",
        },
        {
          target_field: "contact.lastName",
          source_path: "patient_record.last_name",
        },
        {
          target_field: "contact.phone",
          source_path: "patient_record.phone",
          default_value: "",
        },
        {
          target_field: "contact.location",
          source_path: "patient_record.location",
        },
        {
          target_field: "contact.proceduresOfInterest",
          source_path: "patient_record.procedures_of_interest",
        },
        {
          target_field: "contact.photoUrls[0]",
          source_path: "photo_urls[0]",
        },
        {
          target_field: "contact.photoUrls[1]",
          source_path: "photo_urls[1]",
        },
        {
          target_field: "contact.email",
          source_path: "patient_record.email",
        },
        {
          target_field: "clinic.id",
          source_path: "clinic_uuid",
        },
        {
          target_field: "form.submittedAt",
          source_path: "form_data.submitted_at",
        },
        {
          target_field: "form.note",
          literal_value: "Lead created from Clinicos",
        },
      ],
    },
  },
  {
    id: "liine",
    name: "Liine CRM",
    description: "Send form submissions to CRM automatically.",
    config: {
      webhook_url:
        "https://alice.liine.com/api/form/695cd193-fc80-4dad-8694-d263cdee7e06/lead",
      field_mappings: [
        {
          target_field: "lead.first_name",
          source_path: "patient_record.first_name",
        },
        {
          target_field: "lead.last_name",
          source_path: "patient_record.last_name",
        },
        {
          target_field: "lead.phone_number",
          source_path: "patient_record.phone",
        },
        {
          target_field: "lead.email",
          source_path: "patient_record.email",
        },
        {
          target_field: "lead.form_name",
          source_path: "form_data.formName",
        },
        {
          target_field: "lead.photo_urls[0]",
          source_path: "photo_urls[0]",
          include_if_none: false,
        },
        {
          target_field: "lead.photo_urls[1]",
          source_path: "photo_urls[1]",
          include_if_none: false,
        },
        {
          target_field: "lead.location",
          source_path: "patient_record.location",
        },
        {
          target_field: "lead.procedures_of_interest",
          source_path: "patient_record.procedures_of_interest",
        },
        {
          target_field: "lead.source",
          default_value: "Clinicos Form Submission",
        },
      ],
    },
  },
  {
    id: "slack",
    name: "Slack Notification",
    description: "Sends a formatted message to a Slack channel.",
    config: {
      webhook_url: "https://hooks.slack.com/services/T0000/B0000/XYZ",
      headers: {
        "Content-Type": "application/json",
      },
      field_mappings: [
        {
          target_field: "text",
          literal_value: "🎉 New Clinicos submission received!",
        },
        {
          target_field: "attachments[0].title",
          source_path: "patient_record.first_name",
        },
        {
          target_field: "attachments[0].fields[0].title",
          literal_value: "Email",
        },
        {
          target_field: "attachments[0].fields[0].value",
          source_path: "patient_record.email",
          default_value: "N/A",
        },
        {
          target_field: "attachments[0].fields[1].title",
          literal_value: "Phone",
        },
        {
          target_field: "attachments[0].fields[1].value",
          source_path: "patient_record.phone",
          default_value: "N/A",
        },
        {
          target_field: "attachments[0].fields[2].title",
          literal_value: "Location",
        },
        {
          target_field: "attachments[0].fields[2].value",
          source_path: "patient_record.location",
          default_value: "N/A",
        },
        {
          target_field: "attachments[0].fields[3].title",
          literal_value: "Procedures of Interest",
        },
        {
          target_field: "attachments[0].fields[3].value",
          source_path: "patient_record.procedures_of_interest",
          default_value: "N/A",
        },
      ],
    },
  },
];

const makeId = (prefix: string) =>
  `${prefix}-${Math.random().toString(36).slice(2, 9)}`;

const parsePathTokens = (path: string): (string | number)[] => {
  const tokens: (string | number)[] = [];
  const regex = /[^.[\]]+|\[(\d+)\]/g;
  let match: RegExpExecArray | null;
  // eslint-disable-next-line no-cond-assign
  while ((match = regex.exec(path))) {
    if (match[1] !== undefined) {
      tokens.push(parseInt(match[1], 10));
    } else {
      tokens.push(match[0]);
    }
  }
  return tokens;
};

const getValueAtPath = (data: any, path: string) => {
  if (!path) return undefined;
  return parsePathTokens(path).reduce((acc, token) => {
    if (acc == null) return undefined;
    return acc[token as keyof typeof acc];
  }, data);
};

const setValueAtPath = (
  target: Record<string, any>,
  path: string,
  value: any,
) => {
  const tokens = parsePathTokens(path);
  if (tokens.length === 0) return;

  let current: any = target;

  tokens.forEach((token, index) => {
    const isLast = index === tokens.length - 1;
    if (isLast) {
      if (typeof token === "number") {
        if (!Array.isArray(current)) {
          if (current && typeof current === "object") {
            current[token] = value;
          }
        } else {
          current[token] = value;
        }
      } else {
        current[token] = value;
      }
      return;
    }

    const nextToken = tokens[index + 1];
    if (typeof token === "number") {
      if (!Array.isArray(current)) {
        if (current && typeof current === "object") {
          if (current[token] == null) {
            current[token] = typeof nextToken === "number" ? [] : {};
          }
          current = current[token];
        }
      } else {
        if (current[token] == null) {
          current[token] = typeof nextToken === "number" ? [] : {};
        }
        current = current[token];
      }
    } else {
      if (current[token] == null) {
        current[token] = typeof nextToken === "number" ? [] : {};
      }
      current = current[token];
    }
  });
};

const buildPreviewPayload = (
  mappings: TFieldMappingRow[],
  sampleSubmission: any,
) => {
  const payload: Record<string, any> = {};

  mappings.forEach((row) => {
    const target = row.target_field.trim();
    if (!target) return;

    const literal = row.literal_value.trim();
    const hasLiteral = literal.length > 0;
    const source = row.source_path.trim();

    let resolvedValue: any = undefined;

    if (hasLiteral) {
      resolvedValue = literal;
    } else if (source) {
      resolvedValue = getValueAtPath(sampleSubmission, source);
    }

    if (
      (resolvedValue === undefined ||
        resolvedValue === null ||
        resolvedValue === "") &&
      !hasLiteral
    ) {
      if (row.default_value.trim().length > 0) {
        resolvedValue = row.default_value;
      }
    }

    const shouldInclude =
      resolvedValue !== undefined &&
      resolvedValue !== null &&
      resolvedValue !== ""
        ? true
        : row.include_if_none;

    if (!shouldInclude) {
      return;
    }

    setValueAtPath(payload, target, resolvedValue ?? null);
  });

  return payload;
};

const GenericWebhookSetupCard: React.FC<Props> = ({ session, onSuccess }) => {
  const [webhookUrl, setWebhookUrl] = useState("");
  const [httpMethod] = useState<"POST">("POST");
  const [headers, setHeaders] = useState<THeaderRow[]>([
    { id: makeId("header"), key: "", value: "" },
  ]);
  const [fieldMappings, setFieldMappings] = useState<TFieldMappingRow[]>(() =>
    buildDefaultMappings(),
  );
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [sampleInput, setSampleInput] = useState(
    JSON.stringify(DEFAULT_SAMPLE_SUBMISSION, null, 2),
  );
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusType, setStatusType] = useState<"success" | "error" | null>(
    null,
  );
  const [activeMappingForSource, setActiveMappingForSource] = useState<
    string | null
  >(null);
  const [customSourcePath, setCustomSourcePath] = useState("");
  const [advancedMap, setAdvancedMap] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setAdvancedMap((previous) => {
      const next: Record<string, boolean> = {};
      let changed = false;

      fieldMappings.forEach((row) => {
        const prevValue = previous[row.id] ?? false;
        next[row.id] = prevValue;
        if (previous[row.id] !== prevValue) {
          changed = true;
        }
      });

      if (!changed) {
        const prevKeys = Object.keys(previous);
        const nextKeys = Object.keys(next);
        if (prevKeys.length !== nextKeys.length) {
          changed = true;
        } else {
          for (const key of prevKeys) {
            if (!(key in next)) {
              changed = true;
              break;
            }
          }
        }
      }

      return changed ? next : previous;
    });
  }, [fieldMappings]);

  const parsedSample = useMemo(() => {
    try {
      return JSON.parse(sampleInput);
    } catch {
      return null;
    }
  }, [sampleInput]);

  const staticSourcePathCategories: TSourcePathCategory[] = useMemo(
    () => [
      {
        id: "root",
        title: "Root Fields",
        options: [
          { value: "form_data", label: "Form Data (object)" },
          { value: "photo_urls", label: "Photo URLs (array)" },
        ],
      },
      {
        id: "patient_record",
        title: "Patient Record (form submission)",
        options: [
          { value: "patient_record.first_name", label: "First name" },
          { value: "patient_record.last_name", label: "Last name" },
          { value: "patient_record.email", label: "Email" },
          { value: "patient_record.phone", label: "Phone" },
          { value: "patient_record.sex", label: "Sex" },
          {
            value: "patient_record.areas_of_interest",
            label: "Areas of interest",
          },
          {
            value: "patient_record.procedures_of_interest",
            label: "Procedures of interest",
          },
          { value: "patient_record.upload_type", label: "Upload type" },
          { value: "patient_record.location", label: "Location" },
        ],
      },
    ],
    [],
  );

  const dynamicSourcePathCategories: TSourcePathCategory[] = useMemo(() => {
    const categories: TSourcePathCategory[] = [];

    const formDataEntries =
      parsedSample && parsedSample.form_data
        ? Object.keys(parsedSample.form_data).map((key) => ({
            value: `form_data.${key}`,
            label: `form_data.${key}`,
            description: `Field from submission (form_data.${key})`,
          }))
        : [];

    if (formDataEntries.length > 0) {
      categories.push({
        id: "form_data",
        title: "Form Data Fields",
        options: formDataEntries,
      });
    }

    const targetPhotoCount =
      parsedSample && Array.isArray(parsedSample.photo_urls)
        ? Math.max(parsedSample.photo_urls.length, 3)
        : 3;

    categories.push({
      id: "photo_urls",
      title: "Photo URLs",
      options: Array.from({ length: targetPhotoCount }, (_, index) => ({
        value: `photo_urls[${index}]`,
        label: `photo_urls[${index}]`,
        description: "Photo URL (index may be empty)",
      })),
    });

    return categories;
  }, [parsedSample]);

  const sourcePathCategories = useMemo(() => {
    const combined = [
      ...staticSourcePathCategories,
      ...dynamicSourcePathCategories,
    ];

    return combined.map((category) => {
      const seen = new Set<string>();
      const options = category.options.filter((option) => {
        if (seen.has(option.value)) return false;
        seen.add(option.value);
        return true;
      });
      return { ...category, options };
    });
  }, [staticSourcePathCategories, dynamicSourcePathCategories]);

  const previewPayload = useMemo(() => {
    if (!parsedSample) return null;
    return buildPreviewPayload(fieldMappings, parsedSample);
  }, [fieldMappings, parsedSample]);

  const duplicateTargets = useMemo(() => {
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    fieldMappings.forEach((row) => {
      const target = row.target_field.trim();
      if (!target) return;
      if (seen.has(target)) {
        duplicates.add(target);
      } else {
        seen.add(target);
      }
    });
    return Array.from(duplicates);
  }, [fieldMappings]);

  const applyTemplate = (templateId: string) => {
    setStatusMessage(null);
    setStatusType(null);
    const template = WEBHOOK_TEMPLATES.find((item) => item.id === templateId);
    if (!template) return;

    setSelectedTemplate(templateId);
    const { config } = template;
    setWebhookUrl(config.webhook_url || "");

    const templateHeaders = config.headers
      ? Object.entries(config.headers).map(([key, value]) => ({
          id: makeId("header"),
          key,
          value,
        }))
      : [];
    setHeaders(
      templateHeaders.length > 0
        ? templateHeaders
        : [{ id: makeId("header"), key: "", value: "" }],
    );

    const templateMappings =
      config.field_mappings?.map((mapping) => ({
        id: makeId("mapping"),
        target_field: mapping.target_field ?? "",
        source_path: mapping.source_path ?? "",
        default_value: mapping.default_value ?? "",
        literal_value: mapping.literal_value ?? "",
        include_if_none: Boolean(mapping.include_if_none),
      })) ?? [];

    const normalized =
      templateMappings.length > 0 ? templateMappings : buildDefaultMappings();

    setFieldMappings(ensureRequiredMappings(normalized));
  };

  const updateHeader = (id: string, key: keyof THeaderRow, value: string) => {
    setHeaders((rows) =>
      rows.map((row) => (row.id === id ? { ...row, [key]: value } : row)),
    );
  };

  const updateMapping = (
    id: string,
    key: keyof TFieldMappingRow,
    value: string | boolean,
  ) => {
    setFieldMappings((rows) =>
      rows.map((row) => (row.id === id ? { ...row, [key]: value } : row)),
    );
  };

  const openSourceSelector = (mappingId: string) => {
    const mapping = fieldMappings.find((row) => row.id === mappingId);
    setCustomSourcePath(mapping?.source_path ?? "");
    setActiveMappingForSource(mappingId);
  };

  const applySourceSelection = (mappingId: string, value: string) => {
    updateMapping(mappingId, "source_path", value);
    setActiveMappingForSource(null);
    setCustomSourcePath("");
  };

  const closeSourceSelector = () => {
    setActiveMappingForSource(null);
    setCustomSourcePath("");
  };

  const handleToggleAdvanced = (mappingId: string) => {
    setAdvancedMap((prev) => ({
      ...prev,
      [mappingId]: !(prev[mappingId] ?? false),
    }));
  };

  const addHeaderRow = () => {
    setStatusMessage(null);
    setStatusType(null);
    setHeaders((rows) => [
      ...rows,
      { id: makeId("header"), key: "", value: "" },
    ]);
  };

  const removeHeaderRow = (id: string) => {
    setHeaders((rows) =>
      rows.length <= 1 ? rows : rows.filter((row) => row.id !== id),
    );
  };

  const addMappingRow = () => {
    setStatusMessage(null);
    setStatusType(null);
    setFieldMappings((rows) => [
      ...rows,
      {
        id: makeId("mapping"),
        target_field: "",
        source_path: "",
        default_value: "",
        literal_value: "",
        include_if_none: false,
      },
    ]);
  };

  const removeMappingRow = (id: string) => {
    setFieldMappings((rows) =>
      rows.length <= 1 ? rows : rows.filter((row) => row.id !== id),
    );
  };

  const validateUrl = (url: string) => {
    try {
      const parsed = new URL(url);
      return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch {
      return false;
    }
  };

  const buildRequestPayload = () => {
    const errors: string[] = [];
    const trimmedUrl = webhookUrl.trim();

    if (!trimmedUrl) {
      errors.push("Webhook URL is required.");
    } else if (!validateUrl(trimmedUrl)) {
      errors.push("Webhook URL must be a valid HTTP or HTTPS URL.");
    }

    if (duplicateTargets.length > 0) {
      errors.push(
        `Duplicate target fields detected: ${duplicateTargets.join(", ")}`,
      );
    }

    const sanitizedMappings: TWebhookFieldMapping[] = [];

    fieldMappings.forEach((row, index) => {
      const target = row.target_field.trim();
      const source = row.source_path.trim();
      const literal = row.literal_value.trim();
      const defaultValue = row.default_value.trim();

      if (!target) {
        errors.push(`Mapping #${index + 1} is missing a target field.`);
        return;
      }

      if (!source && !literal && !defaultValue) {
        errors.push(
          `Mapping for "${target}" must specify a source path, literal value, or default value.`,
        );
        return;
      }

      sanitizedMappings.push({
        target_field: target,
        source_path: source || null,
        literal_value: literal || null,
        default_value: defaultValue || null,
        include_if_none: row.include_if_none,
      });
    });

    if (sanitizedMappings.length === 0) {
      errors.push("Add at least one field mapping before saving.");
    }

    const sanitizedHeaders = headers.reduce<Record<string, string>>(
      (acc, row) => {
        const key = row.key.trim();
        if (!key) return acc;
        acc[key] = row.value;
        return acc;
      },
      {},
    );

    const config: TWebhookIntegrationConfig = {
      webhook_url: trimmedUrl,
      http_method: httpMethod,
      headers: Object.keys(sanitizedHeaders).length
        ? sanitizedHeaders
        : undefined,
      field_mappings: sanitizedMappings,
    };

    const payload: TCreateIntegrationRequest = {
      integration_type: IntegrationType.GENERIC_WEBHOOK,
      config,
      is_active: true,
    };

    return { payload, errors };
  };

  const resetForm = () => {
    setWebhookUrl("");
    setHeaders([{ id: makeId("header"), key: "", value: "" }]);
    setFieldMappings(buildDefaultMappings());
    setSelectedTemplate(null);
  };

  const handleSubmit = async () => {
    if (!session) return;
    const result = buildRequestPayload();
    if (!result) return;

    const { payload, errors } = result;

    if (errors.length > 0) {
      const joined = errors.join("\n");
      setStatusMessage(joined);
      setStatusType("error");
      Alert.alert("Fix Validation Issues", joined);
      return;
    }

    try {
      setLoading(true);
      setStatusMessage(null);
      setStatusType(null);
      await createIntegration(session, payload);
      Alert.alert(
        "Generic Webhook Saved",
        "Your webhook integration was created successfully.",
      );
      setStatusMessage("Webhook integration saved successfully.");
      setStatusType("success");
      resetForm();
      onSuccess?.();
    } catch (error) {
      console.error("Error creating webhook integration:", error);
      const message =
        error instanceof Error ? error.message : "Failed to save webhook.";
      setStatusMessage(message);
      setStatusType("error");
      Alert.alert("Error", message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>🧩 Generic Webhook</Text>
      <Text style={styles.subtitle}>
        Send Clinicos form submissions to any external system by calling your
        webhook endpoint.
      </Text>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>
          1. Start with a template (optional)
        </Text>
        <View style={styles.templateContainer}>
          {WEBHOOK_TEMPLATES.map((template) => {
            const isActive = selectedTemplate === template.id;
            return (
              <TouchableOpacity
                key={template.id}
                style={[
                  styles.templateCard,
                  isActive && styles.templateCardActive,
                ]}
                onPress={() => applyTemplate(template.id)}
                activeOpacity={0.85}
              >
                <Text style={styles.templateName}>{template.name}</Text>
                <Text style={styles.templateDescription}>
                  {template.description}
                </Text>
                <View style={styles.templateStatusPill}>
                  <Text
                    style={[
                      styles.templateStatusText,
                      isActive && styles.templateStatusTextActive,
                    ]}
                  >
                    {isActive ? "Selected" : "Tap to use"}
                  </Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>2. Webhook configuration</Text>
        <Text style={styles.label}>Destination URL *</Text>
        <TextInput
          style={styles.input}
          placeholder="https://example.com/webhook-endpoint"
          value={webhookUrl}
          onChangeText={setWebhookUrl}
          autoCapitalize="none"
        />

        <Text style={styles.label}>HTTP Method</Text>
        <TextInput
          style={[styles.input, styles.disabledInput]}
          editable={false}
          value={httpMethod}
        />

        <Text style={[styles.label, styles.headersLabel]}>
          Headers (optional)
        </Text>
        {headers.map((row) => (
          <View key={row.id} style={styles.inlineRow}>
            <TextInput
              style={[styles.input, styles.inlineInput]}
              placeholder="Header Name"
              value={row.key}
              onChangeText={(value) => updateHeader(row.id, "key", value)}
              autoCapitalize="none"
            />
            <TextInput
              style={[styles.input, styles.inlineInput]}
              placeholder="Header Value"
              value={row.value}
              onChangeText={(value) => updateHeader(row.id, "value", value)}
              autoCapitalize="none"
            />
            <View style={styles.removeButton}>
              <Button
                title="✕"
                color="#dc3545"
                onPress={() => removeHeaderRow(row.id)}
                disabled={headers.length <= 1}
              />
            </View>
          </View>
        ))}
        <Button title="+ Add Header" color="#6c757d" onPress={addHeaderRow} />
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>3. Field mappings</Text>
        <Text style={styles.helperText}>
          Map Clinicos data to the JSON payload you want to send. Use dotted
          paths for nested fields (e.g. payload.contact.name) or array notation
          (photos[0]).
        </Text>
        {fieldMappings.map((row, index) => {
          const showAdvanced = advancedMap[row.id] ?? false;

          return (
            <View key={row.id} style={styles.mappingCard}>
              <Text style={styles.mappingTitle}>Mapping #{index + 1}</Text>
              <Text style={styles.label}>Target field *</Text>
              <TextInput
                style={styles.input}
                placeholder="e.g. contact.firstName"
                value={row.target_field}
                onChangeText={(value) =>
                  updateMapping(row.id, "target_field", value)
                }
                autoCapitalize="none"
              />

              <Text style={styles.label}>Source path</Text>
              <TouchableOpacity
                style={[
                  styles.selectInput,
                  row.literal_value.trim() ? styles.disabledInput : null,
                ]}
                onPress={() => openSourceSelector(row.id)}
                disabled={Boolean(row.literal_value.trim())}
              >
                <Text
                  style={
                    row.source_path
                      ? styles.selectInputText
                      : styles.selectInputPlaceholder
                  }
                >
                  {row.source_path || "Select a source field"}
                </Text>
              </TouchableOpacity>
              <View style={styles.inlineRow}>
                <Button
                  title="Clear"
                  color="#dc3545"
                  onPress={() => updateMapping(row.id, "source_path", "")}
                />
              </View>
              {row.literal_value.trim() ? (
                <Text style={styles.helperText}>
                  Source path selection disabled while a literal value is
                  provided.
                </Text>
              ) : (
                <Text style={styles.helperText}>
                  Pick from available submission fields. Need something custom?
                  Choose it from the selector.
                </Text>
              )}

              <TouchableOpacity
                style={styles.advancedToggle}
                onPress={() => handleToggleAdvanced(row.id)}
              >
                <Text style={styles.advancedToggleText}>Advanced</Text>
                <Text style={styles.advancedToggleIcon}>
                  {showAdvanced ? "▲" : "▼"}
                </Text>
              </TouchableOpacity>

              {showAdvanced && (
                <View style={styles.advancedSection}>
                  <Text style={styles.label}>Default value</Text>
                  <TextInput
                    style={styles.input}
                    placeholder="Used when source is empty"
                    value={row.default_value}
                    onChangeText={(value) =>
                      updateMapping(row.id, "default_value", value)
                    }
                    autoCapitalize="none"
                  />

                  <Text style={styles.label}>Literal value</Text>
                  <TextInput
                    style={styles.input}
                    placeholder='Overrides source path (e.g. "Lead created from Clinicos")'
                    value={row.literal_value}
                    onChangeText={(value) =>
                      updateMapping(row.id, "literal_value", value)
                    }
                    autoCapitalize="none"
                  />
                </View>
              )}

              <View style={styles.switchRow}>
                <Text style={styles.label}>Include even if value is null?</Text>
                <Switch
                  value={row.include_if_none}
                  onValueChange={(value) =>
                    updateMapping(row.id, "include_if_none", value)
                  }
                />
              </View>

              <View style={styles.removeButton}>
                <Button
                  title="Remove Mapping"
                  color="#dc3545"
                  onPress={() => removeMappingRow(row.id)}
                  disabled={fieldMappings.length <= 1}
                />
              </View>
            </View>
          );
        })}

        <Button title="+ Add Mapping" color="#6c757d" onPress={addMappingRow} />

        {duplicateTargets.length > 0 && (
          <Text style={styles.warningText}>
            Duplicate targets: {duplicateTargets.join(", ")}. Each target should
            be unique to avoid overwriting values.
          </Text>
        )}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>4. Preview payload</Text>
        <Text style={styles.helperText}>
          Provide sample submission data (JSON) to see what will be sent.
        </Text>
        <TextInput
          style={[styles.input, styles.textArea]}
          value={sampleInput}
          onChangeText={setSampleInput}
          multiline
          autoCapitalize="none"
        />
        {!parsedSample && (
          <Text style={styles.warningText}>
            Unable to parse sample JSON. Fix the JSON above to see a preview.
          </Text>
        )}
        <Text style={[styles.label, styles.previewLabel]}>
          Generated Payload
        </Text>
        <View style={styles.previewBox}>
          <Text style={styles.previewText}>
            {previewPayload
              ? JSON.stringify(previewPayload, null, 2)
              : "Preview will appear here once the sample JSON is valid."}
          </Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>5. Save integration</Text>
        <Text style={styles.helperText}>
          We will create a new Generic Webhook integration for this clinic.
        </Text>
        {statusMessage && (
          <Text
            style={[
              styles.statusMessage,
              statusType === "success"
                ? styles.statusSuccess
                : styles.statusError,
            ]}
          >
            {statusMessage}
          </Text>
        )}
        <Button
          title={loading ? "Saving..." : "Save Webhook Integration"}
          color="#28a745"
          onPress={handleSubmit}
          disabled={loading}
        />
        {loading && (
          <View style={styles.loadingIndicator}>
            <ActivityIndicator size="small" color="#28a745" />
          </View>
        )}
      </View>

      <SourcePathModal
        visible={Boolean(activeMappingForSource)}
        categories={sourcePathCategories}
        onClose={closeSourceSelector}
        onSelect={(value) => {
          if (activeMappingForSource) {
            applySourceSelection(activeMappingForSource, value);
          }
        }}
        customValue={customSourcePath}
        onChangeCustom={setCustomSourcePath}
      />
    </View>
  );
};

type SourcePathModalProps = {
  visible: boolean;
  categories: TSourcePathCategory[];
  onClose: () => void;
  onSelect: (value: string) => void;
  customValue: string;
  onChangeCustom: (value: string) => void;
};

const SourcePathModal: React.FC<SourcePathModalProps> = ({
  visible,
  categories,
  onClose,
  onSelect,
  customValue,
  onChangeCustom,
}) => {
  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={onClose}
    >
      <View style={styles.modalBackdrop}>
        <View style={styles.modalContent}>
          <Text style={styles.modalTitle}>Select Source Field</Text>
          <ScrollView>
            {categories.map((category) => (
              <View key={category.id} style={styles.modalSection}>
                <Text style={styles.modalSectionTitle}>{category.title}</Text>
                {category.options.map((option) => (
                  <TouchableOpacity
                    key={option.value}
                    style={styles.modalOption}
                    onPress={() => onSelect(option.value)}
                  >
                    <Text style={styles.modalOptionLabel}>{option.label}</Text>
                    <Text style={styles.modalOptionValue}>{option.value}</Text>
                    {option.description ? (
                      <Text style={styles.modalOptionDescription}>
                        {option.description}
                      </Text>
                    ) : null}
                  </TouchableOpacity>
                ))}
              </View>
            ))}
          </ScrollView>
          <View style={styles.modalDivider} />
          <Text style={styles.modalSubtitle}>Need a custom path?</Text>
          <TextInput
            style={styles.input}
            placeholder="Enter custom source path"
            value={customValue}
            onChangeText={onChangeCustom}
            autoCapitalize="none"
          />
          <View style={styles.modalActions}>
            <Button
              title="Use Custom Path"
              color="#28a745"
              onPress={() => {
                const trimmed = customValue.trim();
                if (!trimmed) {
                  Alert.alert(
                    "Missing path",
                    "Enter a custom source path before using it.",
                  );
                  return;
                }
                onSelect(trimmed);
              }}
            />
            <Button title="Cancel" color="#6c757d" onPress={onClose} />
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    padding: 20,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: "#dee2e6",
  },
  title: {
    fontSize: 24,
    fontWeight: "700",
    marginBottom: 8,
    color: "#212529",
  },
  subtitle: {
    fontSize: 15,
    color: "#495057",
    marginBottom: 40,
  },
  section: {
    marginBottom: 32,
    marginTop: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 12,
    color: "#212529",
  },
  label: {
    fontSize: 14,
    fontWeight: "500",
    marginBottom: 6,
    color: "#343a40",
  },
  helperText: {
    fontSize: 12,
    color: "#6c757d",
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: "#ced4da",
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginBottom: 12,
    fontSize: 14,
    backgroundColor: "#fff",
  },
  disabledInput: {
    backgroundColor: "#e9ecef",
    color: "#6c757d",
  },
  headersLabel: {
    marginTop: 4,
  },
  inlineRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
    gap: 8,
  },
  inlineInput: {
    flex: 1,
    marginBottom: 0,
  },
  removeButton: {
    marginLeft: 8,
  },
  advancedToggle: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 6,
    backgroundColor: "#f1f3f5",
    marginBottom: 8,
  },
  advancedToggleText: {
    fontSize: 13,
    fontWeight: "600",
    color: "#343a40",
  },
  advancedToggleIcon: {
    fontSize: 12,
    color: "#495057",
  },
  advancedSection: {
    borderWidth: 1,
    borderColor: "#ced4da",
    borderRadius: 8,
    padding: 12,
    backgroundColor: "#ffffff",
    marginBottom: 12,
  },
  templateContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  templateCard: {
    borderWidth: 1,
    borderColor: "#ced4da",
    borderRadius: 10,
    padding: 12,
    backgroundColor: "#f8f9fa",
    width: "48%",
    minWidth: 160,
  },
  templateCardActive: {
    borderColor: "#28a745",
    backgroundColor: "#e9f7ef",
  },
  templateName: {
    fontSize: 15,
    fontWeight: "600",
    marginBottom: 4,
  },
  templateDescription: {
    fontSize: 12,
    color: "#6c757d",
    marginBottom: 8,
  },
  templateStatusPill: {
    alignSelf: "flex-start",
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: "#dee2e6",
  },
  templateStatusText: {
    fontSize: 11,
    fontWeight: "600",
    color: "#495057",
    textTransform: "uppercase",
  },
  templateStatusTextActive: {
    color: "#1b5e20",
  },
  mappingCard: {
    borderWidth: 1,
    borderColor: "#ced4da",
    borderRadius: 10,
    padding: 12,
    backgroundColor: "#f8f9fa",
    marginBottom: 16,
  },
  mappingTitle: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 8,
    color: "#343a40",
  },
  switchRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  warningText: {
    color: "#dc3545",
    fontSize: 12,
    marginTop: 4,
  },
  textArea: {
    minHeight: 120,
    textAlignVertical: "top",
  },
  previewLabel: {
    marginTop: 8,
  },
  previewBox: {
    borderWidth: 1,
    borderColor: "#adb5bd",
    borderRadius: 8,
    padding: 12,
    backgroundColor: "#212529",
  },
  previewText: {
    color: "#f8f9fa",
    fontFamily: "Courier",
    fontSize: 12,
  },
  selectInput: {
    borderWidth: 1,
    borderColor: "#ced4da",
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 12,
    marginBottom: 8,
    backgroundColor: "#fff",
  },
  selectInputText: {
    fontSize: 14,
    color: "#212529",
  },
  selectInputPlaceholder: {
    fontSize: 14,
    color: "#6c757d",
    fontStyle: "italic",
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.4)",
    justifyContent: "center",
    padding: 20,
  },
  modalContent: {
    backgroundColor: "#fff",
    borderRadius: 12,
    maxHeight: "90%",
    padding: 16,
    borderWidth: 1,
    borderColor: "#dee2e6",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 12,
  },
  modalSubtitle: {
    fontSize: 14,
    fontWeight: "500",
    marginBottom: 8,
  },
  modalSection: {
    marginBottom: 16,
  },
  modalSectionTitle: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 8,
    color: "#495057",
  },
  modalOption: {
    borderWidth: 1,
    borderColor: "#ced4da",
    borderRadius: 8,
    padding: 10,
    backgroundColor: "#f8f9fa",
    marginBottom: 8,
  },
  modalOptionLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#212529",
  },
  modalOptionValue: {
    fontSize: 12,
    color: "#495057",
  },
  modalOptionDescription: {
    fontSize: 12,
    color: "#6c757d",
    marginTop: 4,
  },
  modalDivider: {
    borderBottomWidth: 1,
    borderColor: "#dee2e6",
    marginVertical: 12,
  },
  modalActions: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 12,
  },
  statusMessage: {
    fontSize: 16,
    marginBottom: 12,
    fontWeight: "600",
  },
  statusSuccess: {
    color: "#2f9e44",
  },
  statusError: {
    color: "#d6336c",
  },
  loadingIndicator: {
    marginTop: 12,
  },
});

export default GenericWebhookSetupCard;
